import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, Check, X, FileDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { sendBoardConsentEmail, getBoardMembers } from '@/services/boardService';

interface ResolutionDetailProps {
  resolutionId: string;
  refresh: () => void;
}

interface Resolution {
  id: string;
  company_id: string;
  title: string;
  body_md: string;
  status: string;
  required_role: string;
  quorum_pct: number;
  created_at: string;
  approved_at: string | null;
}

interface Progress {
  resolution_id: string;
  approvals: number;
  rejections: number;
  abstentions: number;
  signed_total: number;
}

export function ResolutionDetail({ resolutionId, refresh }: ResolutionDetailProps) {
  const [resolution, setResolution] = useState<Resolution | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      setLoading(true);
      const [{ data: res, error: resError }, { data: prog, error: progError }] = await Promise.all([
        supabase.from('board_resolutions').select('*').eq('id', resolutionId).single(),
        supabase
          .from('resolution_progress')
          .select('*')
          .eq('resolution_id', resolutionId)
          .maybeSingle(),
      ]);

      if (resError) throw resError;

      setResolution(res);
      setProgress(prog);
    } catch (err) {
      console.error('Error loading resolution:', err);
      toast.error('Failed to load resolution details');
    } finally {
      setLoading(false);
    }
  }

  async function circulate() {
    try {
      const { data, error } = await supabase.rpc('circulate_resolution', {
        p_resolution: resolutionId,
      });

      if (error) throw error;

      toast.success(`Sent to ${data} board member(s)`);
      refresh();
      load();
    } catch (err: any) {
      console.error('Error circulating resolution:', err);
      toast.error(err.message || 'Failed to circulate resolution');
    }
  }

  async function sendEmailNotifications() {
    if (!resolution) return;

    try {
      // Get company name
      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('name')
        .eq('id', resolution.company_id)
        .single();

      if (companyError) throw companyError;

      // Get all board members
      const members = await getBoardMembers(resolution.company_id);

      toast.info(`Sending emails to ${members.length} board member(s)...`);

      // Send emails to all members
      const results = await Promise.allSettled(
        members.map(async (member) => {
          // Get member's email from auth.users
          const { data: userData } = await supabase.auth.admin.getUserById(member.user_id);
          if (userData?.user?.email) {
            return sendBoardConsentEmail(userData.user.email, resolutionId, company.name);
          }
        })
      );

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      toast.success(`Sent ${successCount} email notification(s)`);
    } catch (err: any) {
      console.error('Error sending email notifications:', err);
      toast.error(err.message || 'Failed to send email notifications');
    }
  }

  async function sign(decision: 'APPROVE' | 'REJECT' | 'ABSTAIN') {
    try {
      const { error } = await supabase.rpc('sign_resolution', {
        p_resolution: resolutionId,
        p_decision: decision,
      });

      if (error) throw error;

      toast.success(`You ${decision.toLowerCase()}d the resolution`);
      refresh();
      load();
    } catch (err: any) {
      console.error('Error signing resolution:', err);
      toast.error(err.message || 'Failed to sign resolution');
    }
  }

  async function finalize() {
    try {
      const { data, error } = await supabase.rpc('finalize_resolution', {
        p_resolution: resolutionId,
      });

      if (error) throw error;

      toast.success(`Finalized: ${data}`);
      refresh();
      load();
    } catch (err: any) {
      console.error('Error finalizing resolution:', err);
      toast.error(err.message || 'Failed to finalize resolution');
    }
  }

  async function downloadPDF() {
    // PDF generation will be implemented later
    toast.info('PDF generation coming soon');
  }

  useEffect(() => {
    load();
  }, [resolutionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!resolution) {
    return <p className="p-4 text-sm text-muted-foreground">Resolution not found.</p>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-500';
      case 'REJECTED':
        return 'bg-red-500';
      case 'IN_CIRCULATION':
        return 'bg-blue-500';
      case 'DRAFT':
        return 'bg-gray-500';
      case 'ARCHIVED':
        return 'bg-gray-400';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{resolution.title}</h3>
        <Badge className={getStatusColor(resolution.status)}>{resolution.status}</Badge>
      </div>

      <div className="rounded-md bg-muted p-4">
        <pre className="whitespace-pre-wrap text-sm font-mono">{resolution.body_md}</pre>
      </div>

      {progress && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Approvals</p>
            <p className="text-lg font-semibold text-green-600">{progress.approvals}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Rejections</p>
            <p className="text-lg font-semibold text-red-600">{progress.rejections}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Abstentions</p>
            <p className="text-lg font-semibold">{progress.abstentions}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Total Signed</p>
            <p className="text-lg font-semibold">{progress.signed_total}</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {resolution.status === 'DRAFT' && (
          <>
            <Button onClick={circulate} className="flex items-center gap-2">
              <Send className="h-4 w-4" /> Circulate
            </Button>
            <Button
              onClick={sendEmailNotifications}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" /> Send Email Notifications
            </Button>
          </>
        )}

        {resolution.status === 'IN_CIRCULATION' && (
          <Button
            onClick={sendEmailNotifications}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" /> Resend Notifications
          </Button>
        )}

        {resolution.status === 'IN_CIRCULATION' && (
          <>
            <Button
              onClick={() => sign('APPROVE')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Check className="h-4 w-4" /> Approve
            </Button>
            <Button
              onClick={() => sign('REJECT')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <X className="h-4 w-4" /> Reject
            </Button>
            <Button onClick={() => sign('ABSTAIN')} variant="outline">
              Abstain
            </Button>
          </>
        )}

        <Button onClick={finalize} variant="secondary">
          Finalize
        </Button>

        <Button onClick={downloadPDF} variant="ghost" className="flex items-center gap-2">
          <FileDown className="h-4 w-4" /> PDF
        </Button>
      </div>

      <div className="text-xs text-muted-foreground space-y-1">
        <p>Required Role: {resolution.required_role}</p>
        <p>Quorum: {(resolution.quorum_pct * 100).toFixed(0)}%</p>
        <p>Created: {new Date(resolution.created_at).toLocaleString()}</p>
        {resolution.approved_at && (
          <p>Approved: {new Date(resolution.approved_at).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
}
